<template>
  <div class="loading-errors-section">
    <div v-if="loading" class="loading-state">
      {{ 'color_indication.loading_errors.loading' | translate }}
    </div>

    <template v-else>
      <div class="section-header">
        <p class="section-description">
          {{ 'color_indication.loading_errors.description' | translate }}
        </p>
      </div>

      <div v-if="ingredients.length === 0" class="empty-state">
        <p>{{ 'color_indication.loading_errors.empty_state' | translate }}</p>
      </div>

      <div v-else>
        <!-- Header -->
        <div class="grid-header">
          <div class="grid-header-item header-checkbox">
            <v-switch
              v-model="selectAll"
              :disabled="readonly"
              @input="toggleSelectAll"
              :label="$options.filters.translate('color_indication.loading_errors.select_all')">
            </v-switch>
          </div>
          <div class="grid-header-item header-with-tooltips">
            <div class="tooltip-wrapper">
              <svg class="info-icon-small" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                @mouseenter="showTooltip('percent')" @mouseleave="hideTooltip">
                <circle cx="12" cy="12" r="10" stroke-width="2" />
                <path d="M12 16v-4M12 8h.01" stroke-width="2" stroke-linecap="round" />
              </svg>
              <div v-if="activeTooltip === 'percent'" class="tooltip">
                {{ 'color_indication.loading_errors.tooltip_percent' | translate }}
              </div>
            </div>

            <span class="header-separator"></span>

            <div class="tooltip-wrapper">
              <svg class="info-icon-small" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                @mouseenter="showTooltip('weight')" @mouseleave="hideTooltip">
                <circle cx="12" cy="12" r="10" stroke-width="2" />
                <path d="M12 16v-4M12 8h.01" stroke-width="2" stroke-linecap="round" />
              </svg>
              <div v-if="activeTooltip === 'weight'" class="tooltip">
                {{ 'color_indication.loading_errors.tooltip_weight' | translate }}
              </div>
            </div>
          </div>
        </div>

        <!-- Ingredients -->
        <div v-for="ingredient in ingredients" :key="ingredient.id" class="grid-row">
          <div class="grid-cell-label">
            <v-switch
              v-model="ingredient.enabled"
              :disabled="readonly"
              @input="handleToggleChange(ingredient)">
            </v-switch>
            <span class="ingredient-name">{{ ingredient.ingredient.name }}</span>
          </div>

          <div class="grid-cell-buttons" :class="{ 'is-disabled': !ingredient.enabled }">
            <button class="value-button" :class="{ 'is-active': ingredient.enabled && ingredient.level === 'percent' }"
              :disabled="!ingredient.enabled || readonly" @click="selectType(ingredient, 'percent')">
              <input type="number" v-model.number="ingredient.percent" :disabled="!ingredient.enabled || readonly"
                class="value-input" min="0" max="100" step="0.1" @click.stop @focus="handleFocus(ingredient, 'percent')"
                @blur="handleBlur(ingredient, 'percent')" @input="handleInput(ingredient, 'percent')" />
              <span class="value-unit">{{ 'color_indication.loading_errors.unit_percent' | translate }}</span>
            </button>

            <button class="value-button" :class="{ 'is-active': ingredient.enabled && ingredient.level === 'weight' }"
              :disabled="!ingredient.enabled || readonly" @click="selectType(ingredient, 'weight')">
              <input type="number" v-model.number="ingredient.weight" :disabled="!ingredient.enabled || readonly"
                class="value-input" min="0" max="999" step="0.1" @click.stop @focus="handleFocus(ingredient, 'weight')"
                @blur="handleBlur(ingredient, 'weight')" @input="handleInput(ingredient, 'weight')" />
              <span class="value-unit">{{ 'color_indication.loading_errors.unit_weight' | translate }}</span>
            </button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import api from '@/api'

export default {
  name: 'LoadingErrorsTab',

  props: {
    active: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      loading: false,
      ingredients: [],
      dataLoaded: false,
      isInitializing: true,
      initialData: null,
      activeTooltip: null,
      selectAll: false,
      focusedValues: {}
    }
  },

  computed: {
    farmId () {
      return this.$route.params.farm_id
    }
  },

  watch: {
    active (newVal) {
      if (newVal && this.farmId && !this.dataLoaded) {
        this.loadIngredients()
      }
    },

    farmId (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.dataLoaded = false
        this.ingredients = []

        if (this.active && newVal) {
          this.loadIngredients()
        }
      }
    },

    ingredients: {
      handler () {
        this.updateSelectAllState()
      },
      deep: true
    }
  },

  methods: {
    showTooltip (type) {
      this.activeTooltip = type
    },

    hideTooltip () {
      this.activeTooltip = null
    },

    updateSelectAllState () {
      if (this.ingredients.length === 0) {
        this.selectAll = false
        return
      }

      const allEnabled = this.ingredients.every(ing => ing.enabled)

      this.$nextTick(() => {
        this.selectAll = allEnabled
      })
    },

    toggleSelectAll () {
      if (this.isInitializing || this.readonly) return

      const newState = this.selectAll

      this.ingredients.forEach((ingredient, index) => {
        this.$set(this.ingredients[index], 'enabled', newState)

        if (!newState) {
          this.$set(this.ingredients[index], 'level', null)
        } else {
          this.$set(this.ingredients[index], 'level', 'percent')
          if (!this.ingredients[index].percent || this.ingredients[index].percent === 0) {
            this.$set(this.ingredients[index], 'percent', 3)
          }
        }
      })

      this.$nextTick(() => {
        this.saveBulkIngredients()
      })
    },

    async saveBulkIngredients () {
      try {
        const bulkData = this.ingredients.map(ingredient => ({
          id: ingredient.id,
          level: ingredient.enabled ? ingredient.level : null,
          percent: ingredient.percent || null,
          weight: ingredient.weight || null
        }))

        await api.monitoring.updateIngredientsBulk(bulkData)

        this.$toasted.show('✓ ' + this.$options.filters.translate('color_indication.messages.settings_saved'), {
          duration: 2000,
          position: 'bottom-right',
          type: 'success',
          theme: 'bubble'
        })

        this.saveInitialState()
      } catch (error) {
        console.error('❌ Error saving ingredients:', error)
        this.$toasted.error(this.$options.filters.translate('color_indication.messages.save_error'))
      }
    },

    async loadIngredients () {
      if (!this.farmId) return

      this.loading = true
      this.isInitializing = true

      try {
        const { data } = await api.monitoring.ingredients({
          farm: this.farmId
        })

        let ingredientsData = []
        if (data.data && Array.isArray(data.data)) {
          ingredientsData = data.data
        } else if (data.results && Array.isArray(data.results)) {
          ingredientsData = data.results
        } else if (Array.isArray(data)) {
          ingredientsData = data
        }

        this.ingredients = ingredientsData.map(item => ({
          id: item.id,
          ingredient: item.ingredient,
          level: item.level,
          percent: item.percent || 0,
          weight: item.weight || 0,
          enabled: item.level !== null
        }))

        this.saveInitialState()
        this.dataLoaded = true
        this.updateSelectAllState()

        setTimeout(() => {
          this.isInitializing = false
        }, 500)
      } catch (error) {
        console.error('❌ Error loading ingredients:', error)
        this.$toasted.error(this.$options.filters.translate('color_indication.messages.load_ingredients_error'))
      } finally {
        this.loading = false
      }
    },

    saveInitialState () {
      this.initialData = JSON.stringify(this.ingredients)
    },

    hasChanges () {
      if (!this.initialData) return true
      return JSON.stringify(this.ingredients) !== this.initialData
    },

    handleToggleChange (ingredient) {
      if (this.isInitializing || this.readonly) return

      if (!ingredient.enabled) {
        this.$set(ingredient, 'level', null)
      } else {
        this.$set(ingredient, 'level', 'percent')
        if (!ingredient.percent || ingredient.percent === 0) {
          this.$set(ingredient, 'percent', 3)
        }
      }

      this.$nextTick(() => {
        this.updateSelectAllState()
        this.saveIngredient(ingredient)
      })
    },

    selectType (ingredient, type) {
      if (!ingredient.enabled) return

      this.$set(ingredient, 'level', type)
      this.saveIngredient(ingredient)
    },

    handleFocus (ingredient, type) {
      if (!ingredient.enabled) return

      this.focusedValues = {
        id: ingredient.id,
        type: type,
        value: ingredient[type],
        level: ingredient.level
      }

      this.$set(ingredient, 'level', type)
    },

    handleInput (ingredient, type) {
      if (!ingredient.enabled) return

      if (type === 'percent' && ingredient[type] > 100) {
        this.$set(ingredient, type, 100)
      }
      if (type === 'weight' && ingredient[type] > 999) {
        this.$set(ingredient, type, 999)
      }
      if (ingredient[type] < 0) {
        this.$set(ingredient, type, 0)
      }
    },

    handleBlur (ingredient, type) {
      if (this.isInitializing || !ingredient.enabled || this.readonly) return

      let value = ingredient[type] || 0

      if (type === 'percent') {
        value = Math.min(100, Math.max(0, value))
      } else {
        value = Math.min(999, Math.max(0, value))
      }

      this.$set(ingredient, type, value)
      this.$set(ingredient, 'level', type)

      const hasChanges = this.focusedValues.id === ingredient.id && (
        this.focusedValues.value !== value ||
        this.focusedValues.level !== type
      )

      if (hasChanges) {
        this.saveIngredient(ingredient)
      }

      this.focusedValues = {}
    },

    async saveIngredient (ingredient) {
      try {
        const payload = {
          level: ingredient.enabled ? ingredient.level : null,
          percent: ingredient.percent || null,
          weight: ingredient.weight || null
        }

        await api.monitoring.updateIngredient(ingredient.id, payload)

        this.$toasted.show('✓ ' + this.$options.filters.translate('color_indication.messages.settings_saved'), {
          duration: 2000,
          position: 'bottom-right',
          type: 'success',
          theme: 'bubble'
        })

        this.saveInitialState()
      } catch (error) {
        console.error('❌ Error saving ingredient:', error)
        this.$toasted.error(this.$options.filters.translate('color_indication.messages.save_error'))
      }
    }
  }
}
</script>
<style scoped>
.loading-errors-section {
  padding-right: 40px;
}

.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  color: #666;
}

.section-header {
  margin-bottom: 40px;
}

.section-description {
  font-size: 14px;
  line-height: 1.6;
  color: #000;
  margin: 0;
}

.empty-state {
  padding: 40px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

.grid-header {
  display: grid;
  grid-template-columns: 1fr 155px;
  gap: 16px;
  margin-bottom: 8px;
  align-items: center;
  padding: 10px 0;
}

.grid-header-item {
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 13px;
  color: #666;
  font-weight: 500;
}

.grid-header-item.header-checkbox {
  justify-content: flex-start;
  gap: 0;
}

.header-with-tooltips {
  display: flex;
  align-items: center;
  gap: 24px;
}

.tooltip-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.info-icon-small {
  width: 24px;
  height: 24px;
  color: #1DA643;
  cursor: help;
  transition: color 0.2s;
}

.info-icon-small:hover {
  color: #45a049;
}

.tooltip {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background: white;
  border: 1px solid #D7D7D7;
  border-radius: 8px;
  padding: 10px 14px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  width: 160px;
  font-size: 12px;
  line-height: 1.5;
  color: #000;
  pointer-events: none;
  white-space: normal;
}

.tooltip::before {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 6px 6px 0 6px;
  border-color: white transparent transparent transparent;
}

.tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 1px;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 7px 7px 0 7px;
  border-color: #D7D7D7 transparent transparent transparent;
  z-index: -1;
}

.grid-row {
  display: grid;
  grid-template-columns: 1fr 155px;
  gap: 16px;
  align-items: center;
  padding: 10px 0;
  font-size: 14px;
  transition: background-color 0.2s;
  border-radius: 8px;
}

.grid-cell-label {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 6px;
}

.ingredient-name {
  flex: 1;
  font-size: 16px;
  font-weight: 900;
  color: #000;
  margin-top: 4px;
}

.grid-cell-buttons {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  padding: 0.4rem;
  background: #EEEEEE;
  border-radius: 1rem;
}

.grid-cell-buttons.is-disabled {
  background: transparent;
}

.value-button {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 4px 4px;
  background: #EEEEEE;
  border: 2px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 70px;
  justify-content: center;
  position: relative;
}

.value-button:disabled {
  cursor: not-allowed;
  background: transparent;
}

.value-button.is-active {
  background: #59CC56;
  border-color: #59CC56;
}

.value-input {
  width: 40px;
  background: transparent;
  border: none;
  outline: none;
  font-size: 18px;
  font-weight: 700;
  color: #000;
  text-align: center;
  padding: 0;
  transition: color 0.2s;
}

.value-button.is-active .value-input {
  color: #000;
}

.value-input:disabled {
  color: #979797;
}

.value-input::-webkit-inner-spin-button,
.value-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.value-input[type=number] {
  -moz-appearance: textfield;
}

.value-unit {
  font-size: 12px;
  font-weight: 700;
  color: #707070;
  transition: color 0.2s;
}

.value-button:disabled .value-unit {
  color: #979797;
}
.v-switch-wrapper--with-label {
  justify-content: unset;
}
</style>